import { evolve, isNil, identity } from 'ramda'

import { stateNameMapping } from '../geolocation'

/**
 * Adds an index property to a doctor so that we can correlate
 * doctors in the list with markers on the map.
 *
 * @param {arr} doctors The list of doctors to index
 */
export const indexDoctors = doctors =>
  // start at 1 so there are no doctor 0's
  doctors.map((doctor, i) => ({ ...doctor, index: i + 1 }))

/**
 * Sometimes the address lat and lon are strings and that causes
 * things to break. This tries to address that by making into numbers.
 *
 * @param {obj} doctor
 */
export const parseAddressLatLngToNumber = evolve({
  address: {
    lat: Number,
    lon: Number,
  },
})

/**
 * Given a list of addresses, returns the one marked primary, or the first one.
 *
 * @param {arr} addresses List of addresses to look through
 */
export const getPrimaryAddress = (addresses = []) => {
  return addresses.reduce((acc, address) => {
    // if there is not yet a chosen address,
    // use the first address given as a default
    if (isNil(acc)) {
      return address
    } else {
      // if there is already a default, check to see
      // if given address is the primary, as opposed to default
      return address.primary ? address : acc
    }
  }, null)
}

/**
 * Given an array of doctors, returns a new list of doctors
 * where now each doctor location is a separate array item.
 *
 * @param {arr} doctors A list of doctors to get addresses from
 */
export const getAllDoctorLocations = doctors => {
  return doctors
    .reduce((acc, doctor = {}) => {
      const { addresses = [] } = doctor

      // putting the lat/lon in the root of the dottor
      // not taking into account whether the address is primary
      // or not

      const allDoctorAddresses = addresses.map(address => {
        return address.lat &&
          address.lat !== 0 &&
          address.lon &&
          address.lon !== 0
          ? {
              ...doctor,
              address,
              lat: address.lat,
              lon: address.lon,
            }
          : null
      })
      return [...acc, ...allDoctorAddresses]
    }, [])
    .filter(identity)
}

/**
 * This is to mitigate the issue where doctors have offices in disparate
 * locations, i.e. their primary office is in FL but they have another in
 * UT, so when we search for UT location, all the addresses show FL
 * @param {array} addresses doctor addresess list (unsorted)
 * @param {string} region SearchFilter -> filters -> searchLocation
 */
export const determineAddressToShow = (addresses, region) => {
  // 1. sort the addresses by primary
  // 2. loop through the addresses
  // 3. if there is a state use that to find addr that matches that state
  // 4. if there is no state use the region to find addr that matches that state
  // 5. if there is no state or region use primay
  // 6. if there is for whatever reason no primary, use the first address
  // let addressToShow

  const sortedAddresses = addresses.sort((a, b) => {
    if (a.primary) return -1
    if (b.primary) return 1
    return 0
  })

  const addressToShow = sortedAddresses.find(
    addr => addr.state === stateNameMapping[region],
  )

  if (!addressToShow) {
    return sortedAddresses.find(addr => addr.primary)
  }

  return addressToShow
}
